import React, { useState, useEffect } from 'react';
import './StudentOutcomes.css';
import { getAllPrograms } from '../services/api';

// Import images
import accountancyImage from './images/accountancy-image.jpg';
import managementImage from './images/management-image.jpg';
import marketingImage from './images/marketing-image.jpeg';
import operationsImage from './images/operations-image.jpeg';
import supplyChainImage from './images/supply-chain-image.jpeg';
import entrepreneurshipImage from './images/entrepreneurship-image.jpeg';

const StudentOutcomes = ({ onBack, sidebarExpanded, onProgramSelect }) => {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  // Initialize school from URL parameter to avoid double API calls
  const [school, setSchool] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('school') || 'MARYLAND';
  });
  const [schoolDisplayName, setSchoolDisplayName] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('school') || 'MARYLAND';
  });
  const [term, setTerm] = useState('2025 Spring');

  // This useEffect is no longer needed since we initialize from URL
  // Keeping it empty for potential future URL change handling
  // useEffect(() => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const schoolParam = urlParams.get('school');
  //   if (schoolParam) {
  //     setSchool(schoolParam);
  //     setSchoolDisplayName(schoolParam);
  //   }
  // }, []);

  useEffect(() => {
    const fetchPrograms = async () => {
      setIsLoading(true);
      setError(null); // Clear any previous error immediately to prevent flicker
      setShowError(false); // Reset error display state
      try {
        //console.log('Fetching programs with school:', school);
        const data = await getAllPrograms(school);
        //console.log('API Response:', data);
        setPrograms(data.programs || []);
        setError(null);
      } catch (error) {
        console.error('Error fetching programs:', error);
        if (error.response && error.response.status === 500) {
          setError(`Server error (500). API request failed with school="${school}". Using mock data instead.`);
        } else {
          setError('Failed to load programs. Using mock data instead.');
        }
        setPrograms(mockPrograms);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrograms();
  }, [school]);

  // Delayed error display to prevent flicker during token refresh
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (error && !isLoading && programs.length === 0) {
        setShowError(true);
      }
    }, 500); // Delay 500ms before showing error

    return () => clearTimeout(timeoutId);
  }, [error, isLoading, programs.length]);

  const handleSchoolChange = (displayName) => {
    setSchoolDisplayName(displayName);
    const schoolMapping = {
      'MARYLAND': 'MARYLAND',
    };
    setSchool(schoolMapping[displayName] || 'MARYLAND');
  };

  const mockPrograms = [
  ];

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleStartAssessment = (programId) => {
    const selectedProgram = programs.find(program => program.id === programId);
    if (selectedProgram && onProgramSelect) {
      onProgramSelect(selectedProgram);
    }
  };

  const getProgramImage = (program) => {
    if (program.image) return program.image;
    
    const name = program.name.toLowerCase();
    if (name.includes('accountancy') || name.includes('accounting')) return accountancyImage;
    if (name.includes('management')) return managementImage;
    if (name.includes('marketing')) return marketingImage;
    if (name.includes('operations')) return operationsImage;
    if (name.includes('supply chain')) return supplyChainImage;
    if (name.includes('entrepreneurship') || name.includes('strategy')) return entrepreneurshipImage;
    
    return accountancyImage;
  };

  const filteredPrograms = programs.filter(program => {
    const matchesSearch = program.name.toLowerCase().includes(searchTerm.toLowerCase());
    const programType = program.type || program.category || '';
    const matchesFilter = filter === 'All' || programType.includes(filter);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="dashboard student-outcomes">
      <div className="filters">
        <div className="filter">
          <img src="/school.svg" alt="School" className="filter-icon" />
          <select 
            className="filter-select"
            value={schoolDisplayName}
            onChange={(e) => handleSchoolChange(e.target.value)}
          >
            <option value={schoolDisplayName}>{schoolDisplayName}</option>
          </select>
        </div>
        
        <div className="filter">
          <img src="/page-1-calendar.svg" alt="Calendar" className="filter-icon" />
          <select 
            className="filter-select"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          >
            <option>2025 Spring</option>
          </select>
        </div>
      </div>

      <div className="outcomes-header">
        <h1>View Students Outcomes By Program</h1>
      </div>

      {showError && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search programs..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>

      <div className="filter-buttons">
        <button 
          className={`filter-button ${filter === 'All' ? 'active' : ''}`}
          onClick={() => handleFilterChange('All')}
        >
          All
        </button>
        <button 
          className={`filter-button ${filter === 'Undergraduate' ? 'active' : ''}`}
          onClick={() => handleFilterChange('Undergraduate')}
        >
          Undergraduate
        </button>
        <button 
          className={`filter-button ${filter === 'Graduate' ? 'active' : ''}`}
          onClick={() => handleFilterChange('Graduate')}
        >
          Graduate
        </button>
        <button className="filter-more">
          <img src="/filter-icon.svg" alt="Filter" />
          Filter
        </button>
      </div>

      {isLoading ? (
        <div className="loading-indicator">Loading programs...</div>
      ) : filteredPrograms.length > 0 ? (
        <div className="program-grid">
          {filteredPrograms.map(program => (
            <div key={program.id} className="program-card">
              <div className="program-image">
                <img src={getProgramImage(program)} alt={program.name} />
                <h3>{program.name}</h3>
                <button 
                  className="start-assessment-button-1"

                  onClick={() => handleStartAssessment(program.id)}
                >
                  Start Evaluation <span>&rarr;</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-results">No programs found matching your criteria.</div>
      )}
    </div>
  );
};

export default StudentOutcomes; 